import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import EditFilterModal from 'components/admin/common/modal/filter/EditFilterModal';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';
import usePut from 'core/hooks/api/usePut';
import usePost from 'core/hooks/api/usePost';
import useDelete from 'core/hooks/api/useDelete';
import { typeDetail } from 'core/constant/SELECT_OPTIONS';

const Filters = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createFilterData,
        postError: creatingError,
        postLoading: creating,
        putData: updateFilterData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteFilterData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.FILTERS);

    const { putData: updateFilterDetail, loading: uploadingFilterDetail } = usePut(API_ENDPOINTS.FILTER_OPTIONS);
    const { postData: createFilterDetail, loading: creatingFilterDetail } = usePost(API_ENDPOINTS.FILTER_OPTIONS);
    const { deleteData: deleteFilterDetail, loading: deletingFilterDetail } = useDelete(API_ENDPOINTS.FILTER_OPTIONS);
    const { fetchData: projectData } = useCrud(API_ENDPOINTS.PROJECTS);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseEditFilter = () => {
        setErrors({});
        clearServerErrors();
    };
    const { isOpen: isOpenEditFilter, modalData: selectedFilter, openModal: openEditFilter, closeModal: closedEditFilter } = useModalState({
        onClose: handleCloseEditFilter
    });

    const { isOpen: isOpenDeleteFilter, modalData: FilterToDelete, openModal: openDeleteFilter, closeModal: closedDeleteFilter } = useModalState({
        onClose: handleCloseEditFilter
    });

    const memoizedFilters = useMemo(() => selectedFilter?.details || [], [selectedFilter]);

    const validationRules = [
        { field: 'name', method: 'required' },
        { field: 'type', method: 'required' }
    ];

    const validationFeaturesRules = [
        { field: 'value', method: 'required' },
    ];

    const projectsOptions = useMemo(() => {
        if (projectData?.length > 0) {
            return projectData.map(project => ({
                label: project.title,
                value: project.id
            }));
        } else {
            return [{ label: 'Default', value: 1 }, { label: 'Default', value: 2 }];
        }
    }, [projectData]);

    const handleModalSave = useCallback(async (updatedFilter, features, featuresToDelete) => {
        setSuccessMessage(null);
        clearServerErrors();

        const formErrors = validateForm(updatedFilter, validationRules);
        const featuresErrors = features.map((item, index) => {
            const featureErrors = validateForm(item, validationFeaturesRules);
            return Object.keys(featureErrors).reduce((acc, key) => {
                if (!acc[index]) acc[index] = {};
                acc[index][key] = featureErrors[key];
                return acc;
            }, {});
        }).filter(error => Object.keys(error).length > 0);

        const combinedErrors = { ...formErrors, ...featuresErrors.reduce((acc, curr) => ({ ...acc, ...curr }), {}) };

        if (Object.keys(combinedErrors).length > 0) {
            setErrors(combinedErrors);
            return;
        }

        setErrors({});
        try {
            let message = '';
            let filterId = updatedFilter.id;
            if (filterId) {
                await updateFilterData(filterId, updatedFilter, true);
                message = "Filtro actualizado con éxito.";
            } else {
                const createdFilter = await createFilterData(updatedFilter, true);
                filterId = createdFilter.id;
                message = "Filtro creado con éxito.";
            }

            for (const feature of features) {
                if (feature.value.trim() === '') {
                    continue;
                }
                feature.filter_id = filterId;
                if (feature.id) {
                    await updateFilterDetail(feature.id, feature, true);
                } else {
                    await createFilterDetail(feature, true);
                }
            }

            for (const featureId of featuresToDelete) {
                await deleteFilterDetail(featureId);
            }

            closedEditFilter();
            setErrors({});
            setSuccessMessage(message);
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateFilterData, createFilterData, updateFilterDetail, createFilterDetail, deleteFilterDetail, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newFilter = {
            name: '',
            description: ''
        };
        openEditFilter(newFilter);
    }, []);

    const handleEditClick = useCallback((filter) => {
        openEditFilter(filter);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (FilterToDelete) {
            try {
                await deleteFilterData(FilterToDelete.id);
                setSuccessMessage("Filtro eliminado con éxito.");
                refetch();
                closedDeleteFilter();
            } catch (error) {
                console.error(error);
            }
        }
    }, [FilterToDelete, deleteFilterData, refetch]);

    const columns = useMemo(() => [
        { field: 'name', header: 'Nombre', sortable: true },
        { field: 'type', header: 'Tipo de filtros', sortable: true },
    ], []);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Detalle de Filtro</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Detalle de Filtro
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={handleEditClick}
                onDelete={openDeleteFilter}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="name"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditFilterModal
                show={isOpenEditFilter}
                filter={selectedFilter}
                handleClose={closedEditFilter}
                handleSave={handleModalSave}
                updating={updating || creating || creatingFilterDetail || uploadingFilterDetail || deletingFilterDetail}
                errors={errors}
                requestError={creatingError || updatingError}
                projectsOptions={projectsOptions}
                features={memoizedFilters}
                deleteFeature={deleteFilterDetail}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteFilter}
                onHide={closedDeleteFilter}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(Filters);